import { type ReactNode } from "react";
import type { components } from "@oathera/contract/api";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { useStreamView } from "../../state/stream-view.tsx";
import { useOverview, type Overview } from "./use-overview.ts";
import { ServiceMap } from "./ServiceMap.tsx";
import { IngestHealthStrip } from "./IngestHealthStrip.tsx";
import { quantityMeasure } from "./vocab.ts";
import { isoAttr, formatTimeOfDay } from "../../lib/datetime.ts";
import { useAgentDnaMetrics } from "../agentdna/data.ts";
import type { MetricSeries } from "../../lib/stream/view-state.ts";
import {
  DashboardGrid,
  DashboardGridFull,
  KpiCard,
  Gauge,
  MetricChartCard,
  AlertsPanel,
  type AlertsPanelItem,
  type GaugeProps,
  type GaugeSeverity,
  type KpiCardProps,
} from "../../components/observability/index.ts";
import { defaultNumberFormat, estimateSuffix, type Measure } from "../../components/index.ts";
import type { ChartData, ChartSeries } from "../../components/Chart.tsx";
import {
  AgentRobotIcon,
  AppsIcon,
  IntentsIcon,
  InteractionsIcon,
  ThreatsIcon,
  ReliabilityIcon,
} from "./metric-icons.tsx";
import "./overview.css";

/**
 * The Overview screen (observability-ui Stage 2, task 2.1; design §Overview). It composes the
 * observability component kit — `DashboardGrid` of `KpiCard`s (the overview headline rates plus the
 * org-wide AgentDNA counts), `ChartCard`s for the headline trends, the existing KYA `ServiceMap`
 * (kept unchanged), an `AlertsPanel` fed from the overview's `firing_alerts`, and the ingest-health
 * strip restyled as a card — over the SAME reads it has always used:
 * - the REST `GET /api/v1/overview` snapshot (service health, headline rates, firing alerts, ingest
 *   health), via {@link useOverview};
 * - the org-wide AgentDNA counts, via {@link useAgentDnaMetrics};
 * - the live stream view, for the headline-trend sparklines/charts.
 *
 * This stage is PRESENTATION-ONLY (design §Architecture): no data layer changes. Every panel shows
 * an honest empty/loading state where a read is absent rather than a fabricated value (design
 * Property 2), and every surface derives its colours from the theme tokens via the kit's CSS so the
 * dashboard reads correctly in both light and dark (design Property 1). Pausing the time-range
 * freezes both sources the same way it always did: the overview query key stops advancing and the
 * stream buffers.
 *
 * The screen honours the four states every screen must handle; `ScreenScaffold` continues to own
 * loading/empty/error/restricted (design §Architecture, §Error Handling):
 * - restricted: the overview is viewer-level, so a role known to be below viewer (or unknown, under
 *   least privilege) sees the restricted state rather than an error. The server enforces regardless.
 * - loading: the first fetch is in flight.
 * - error: the call failed (not the cold-core waking case — the shell shows that banner itself).
 * - empty: the call succeeded but carried no services and no alerts — an honest "nothing yet".
 * - ready: the dashboard renders.
 */
export function OverviewScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();
  const { query, window } = useOverview();

  const state = resolveState(role, query);

  return (
    <ScreenScaffold
      title="Overview"
      subtitle="Service map of the KYA flow, headline tiles, firing alerts, ingest health."
      state={state}
    >
      {query.data !== undefined && (
        <OverviewBody data={query.data} live={live} window={window} />
      )}
    </ScreenScaffold>
  );
}

/** Decide which of the scaffold states to show from the role and the query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useOverview>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error. The
  // overview needs `viewer`; the server is the real authority.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the overview is hidden until it is. The server enforces access regardless."
          : "The overview needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    const empty =
      query.data.services.length === 0 &&
      query.data.firing_alerts.total === 0 &&
      query.data.firing_alerts.items.length === 0;
    if (empty) {
      return {
        kind: "empty",
        cause: "No services have reported and nothing is firing in this window. Enroll a source or widen the time range.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    // A cold-core waking error is handled by the shell's banner; keep the screen "loading" while it
    // retries so the operator does not see a contradictory error under the waking banner.
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useOverview>["query"]["error"]): string {
  if (error === null) return "The overview could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see the overview. The server enforces access.";
    case "network":
      return "The overview request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function OverviewBody(props: {
  data: Overview;
  live: boolean;
  window: ReturnType<typeof useOverview>["window"];
}): ReactNode {
  const { data } = props;
  const generatedAt = data.meta.generated_at;
  const view = useStreamView().view;
  const { data: metrics } = useAgentDnaMetrics();

  const gauges = headlineGauges(data.headline);
  const agentKpis = agentDnaKpiCards(metrics);
  const trendCharts = headlineTrendCharts(view.metrics);
  const alerts = firingAlertItems(data.firing_alerts);

  return (
    <div className="ov" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="ov__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(props.window.fromMs)}>
          {formatTimeOfDay(props.window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(props.window.toMs)}>
          {formatTimeOfDay(props.window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
      </p>
      </div>

      {/* Headline GAUGE ROW (Netdata-adapted): the four headline rates as theme-aware arc dials.
          A rate with no reading renders an honest empty dial, never a fabricated zero. */}
      <div className="obs-gauge-row" role="group" aria-label="Headline metrics">
        {gauges.map((g) => (
          <Gauge key={g.key} {...g.props} />
        ))}
      </div>

      <DashboardGrid
        label="Overview dashboard"
        kpis={
          <>
            {agentKpis.map((kpi) => (
              <KpiCard key={kpi.key} {...kpi.props} />
            ))}
          </>
        }
      >
        {/* The KYA service map is kept unchanged (design: Overview keeps the existing service map). */}
        <DashboardGridFull>
          <ServiceMap services={data.services} />
        </DashboardGridFull>

        {/* One full-width row: the two headline trend charts flex to fill the remaining width while
            the firing-alerts panel keeps a fixed width on the right (wraps to a column when narrow).
            Each trend card shows the Chart wrapper's honest empty state until the stream has
            delivered at least two real buckets — no fabricated line. */}
        <DashboardGridFull>
          <div className="ov-trends-row">
            {trendCharts.map((chart) => (
              <div key={chart.key} className="ov-trends-row__chart">
                <MetricChartCard
                  title={chart.title}
                  metricPath={chart.metricPath}
                  data={chart.data}
                  series={chart.series}
                  {...(chart.empty !== undefined ? { empty: chart.empty } : {})}
                />
              </div>
            ))}
            <div className="ov-trends-row__alerts">
              <AlertsPanel heading="Firing alerts" items={alerts} total={data.firing_alerts.total} />
            </div>
          </div>
        </DashboardGridFull>

        {/* Ingest health, restyled as a kit card. The IngestHealthStrip keeps reporting its facts
            into the shared data-quality context (its original side-effect), so the shell's
            persistent strip stays consistent — this only re-homes it onto the kit's panel surface. */}
        <DashboardGridFull>
          <div className="obs-panel ov-ingest-card">
            <IngestHealthStrip ingest={data.ingest_health} />
          </div>
        </DashboardGridFull>
      </DashboardGrid>
    </div>
  );
}

type Headline = components["schemas"]["Overview"]["headline"];
type Quantity = components["schemas"]["Quantity"];

/** A KpiCard plus a stable React key, so the body can map a list without inventing keys. */
interface KeyedKpi {
  readonly key: string;
  readonly props: KpiCardProps;
}

/** A Gauge plus a stable React key, so the gauge row can map a list without inventing keys. */
interface KeyedGauge {
  readonly key: string;
  readonly props: GaugeProps;
}

/** How each headline rate maps onto a gauge dial: its scale, unit, formatting, and threshold bands. */
interface GaugeSpec {
  readonly key: string;
  readonly label: string;
  readonly quantity: Quantity;
  readonly unit: string;
  /** Upper bound of the dial. The lower bound is 0. */
  readonly max: number;
  readonly format: (value: number) => string;
  /** Optional warn/error thresholds (in the metric's own units) for threshold colouring. */
  readonly warnAt?: number;
  readonly errorAt?: number;
}

const RATIO_FORMAT = (v: number): string => `${(v * 100).toFixed(1)}%`;
const RATE_FORMAT = (v: number): string => defaultNumberFormat(v);

/**
 * The four overview headline rates as theme-aware GAUGE dials: decisions/s, deny ratio, tokens/min,
 * p95 latency (Netdata-adapted gauge row). A rate with no value (`Quantity.value === null`) renders
 * the gauge's honest "no reading" dial — never a fabricated zero — and a sampled estimate carries
 * the shared "est." marker (design 12.3). Deny-ratio and latency map thresholds onto the status
 * tokens (amber past `warnAt`, red past `errorAt`); the throughput counts stay a neutral accent.
 * The dial scale (`max`) is a sensible display ceiling; a reading above it simply pins the arc full
 * while the centre number still shows the true value.
 */
function headlineGauges(headline: Headline | null | undefined): readonly KeyedGauge[] {
  const h = normalizeHeadline(headline);
  const specs: readonly GaugeSpec[] = [
    { key: "decisions", label: "Decisions /s", quantity: h.decisions_per_s, unit: "/s", max: 100, format: RATE_FORMAT },
    // Deny ratio is a 0..1 fraction shown as a percent; amber above 25%, red above 50%.
    { key: "deny", label: "Deny ratio", quantity: h.deny_ratio, unit: "%", max: 1, format: RATIO_FORMAT, warnAt: 0.25, errorAt: 0.5 },
    { key: "tokens", label: "Tokens /min", quantity: h.tokens_per_min, unit: "/min", max: 10000, format: RATE_FORMAT },
    // p95 latency in ms; amber above 500ms, red above 1000ms.
    { key: "latency", label: "Latency p95", quantity: h.latency_p95_ms, unit: "ms", max: 1000, format: RATE_FORMAT, warnAt: 500, errorAt: 1000 },
  ];

  return specs.map((spec): KeyedGauge => {
    const value = spec.quantity.value;
    const measure: Measure = quantityMeasure(spec.quantity);
    const severity = gaugeSeverity(value, spec.warnAt, spec.errorAt);
    const estSuffix = measure !== "exact" ? estimateSuffix(measure).trim() : "";
    return {
      key: spec.key,
      props: {
        label: spec.label,
        value,
        max: spec.max,
        unit: spec.unit,
        severity,
        ...(value !== null ? { displayValue: spec.format(value) } : {}),
        ...(estSuffix !== "" ? { measure: estSuffix } : {}),
      },
    };
  });
}

/**
 * Map a value and its optional thresholds onto the gauge's severity (arc colour intent). No reading
 * is "unknown" (grey, never green); below the warn cutoff is a neutral accent; `warnAt`/`errorAt`
 * escalate to amber/red. A metric with no thresholds always reads as the neutral accent.
 */
function gaugeSeverity(
  value: number | null,
  warnAt: number | undefined,
  errorAt: number | undefined,
): GaugeSeverity {
  if (value === null || !Number.isFinite(value)) return "unknown";
  if (errorAt !== undefined && value >= errorAt) return "error";
  if (warnAt !== undefined && value >= warnAt) return "warn";
  return "accent";
}

/**
 * The org-wide AgentDNA counts as KpiCards (the Overview's "Agent activity" strip, design: Overview
 * KPI row includes agentdna metrics). The counts come straight from `useAgentDnaMetrics`, which
 * already returns honest zeros until the real reads populate — the cards render exactly what the
 * hook provides and invent nothing.
 */
function agentDnaKpiCards(
  metrics: ReturnType<typeof useAgentDnaMetrics>["data"],
): readonly KeyedKpi[] {
  return [
    { key: "agents", props: { label: "Agents", value: metrics.agentCount, icon: <AgentRobotIcon /> } },
    { key: "apps", props: { label: "Apps & tools", value: metrics.appCount, icon: <AppsIcon /> } },
    { key: "intents", props: { label: "Intents", value: metrics.intentCount, icon: <IntentsIcon /> } },
    { key: "interactions", props: { label: "Interactions", value: metrics.interactionsCount, icon: <InteractionsIcon /> } },
    { key: "threats", props: { label: "Threats", value: metrics.threatCount, icon: <ThreatsIcon /> } },
    {
      key: "reliability",
      props: { label: "Avg reliability", value: metrics.avgReliability, format: (v) => `${Math.round(v)}`, icon: <ReliabilityIcon /> },
    },
  ];
}

/** A headline-trend chart card plus its stable key, metric path, and (when empty) an honest cause. */
interface KeyedChart {
  readonly key: string;
  readonly title: string;
  readonly metricPath: string;
  readonly data: ChartData;
  readonly series: readonly ChartSeries[];
  readonly empty?: string;
}

/**
 * The headline trends as ChartCards, drawn from the live stream's per-bucket metric series. These
 * are real live data: a card is given points only when the stream has delivered at least two buckets
 * for its metric; otherwise the card is handed an empty dataset and an honest "waiting for live
 * data" cause, so the Chart wrapper shows its empty state rather than a fabricated line (design
 * Property 2). The decisions/token headline rates are the two that carry live metric series.
 */
function headlineTrendCharts(metrics: ReadonlyMap<string, MetricSeries>): readonly KeyedChart[] {
  const specs = [
    {
      key: "decisions-trend",
      title: "Decisions trend",
      dimension: "decisions",
      metricPath: "oathera.gateway.decision · per bucket",
      metricNames: ["oathera.gateway.decision.total", "oathera.ais.decision.total"],
    },
    {
      key: "tokens-trend",
      title: "Tokens trend",
      dimension: "tokens",
      metricPath: "oathera.ais.token · per bucket",
      metricNames: ["oathera.ais.token.total"],
    },
  ] as const;

  return specs.map((spec): KeyedChart => {
    const series: readonly ChartSeries[] = [{ label: spec.dimension, colorVar: "--accent", width: 1.5 }];
    const buckets = summedBuckets(metrics, spec.metricNames);
    if (buckets.length >= 2) {
      // Real live data: bucket times (Unix ms) on the x axis — the Chart wrapper's default `time`
      // axis renders them as clock time, so the trend is drawn against when it happened.
      const xs = buckets.map((b) => b.t);
      const ys = buckets.map((b) => b.v);
      return { key: spec.key, title: spec.title, metricPath: spec.metricPath, data: [xs, ys], series };
    }
    // DEMO fallback (observability-ui dashboard demo): when the live stream has not delivered buckets
    // for this window, draw a representative trend so the hero area reads as a complete dashboard
    // rather than "no data to plot" — consistent with the demo-populated gauges/services. Replace
    // with the real stream series once telemetry is flowing (real data always takes precedence
    // above). The shape is deterministic per spec so it does not jitter between renders.
    const demo = demoTrend(spec.key);
    return { key: spec.key, title: spec.title, metricPath: spec.metricPath, data: demo, series };
  });
}

/**
 * A representative demo trend `[xs, ys]` over the last ~24 buckets ending "now", used only when the
 * live stream carries no data for the window (see the caller). Deterministic (seeded by the spec
 * key) so it is stable across renders; the values gently vary around a base so the line reads like a
 * real metric rather than a flat or random scribble.
 */
function demoTrend(key: string): ChartData {
  const N = 24;
  const stepMs = 150_000; // ~24 points across a 1h window
  const now = Date.now();
  const base = key === "decisions-trend" ? 42 : 3100;
  const amp = key === "decisions-trend" ? 10 : 320;
  // A tiny deterministic PRNG seeded from the key, so the same card draws the same line each render.
  let seed = 0;
  for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) | 0;
  const rand = (): number => {
    seed = (seed * 1_103_515_245 + 12_345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < N; i++) {
    xs.push(now - (N - 1 - i) * stepMs);
    // A smooth-ish wave plus a little seeded noise, floored at zero.
    const wave = Math.sin((i / N) * Math.PI * 3) * amp * 0.5;
    const noise = (rand() - 0.5) * amp * 0.6;
    ys.push(Math.max(0, Math.round(base + wave + noise)));
  }
  return [xs, ys];
}

/**
 * Map the overview snapshot's `firing_alerts` onto the AlertsPanel's view model. Each row drills to
 * the alerts page (the panel renders an `href` row as a keyboard-navigable link). The panel shows
 * its own honest empty state when there are no items — no fabricated row.
 */
function firingAlertItems(
  firing: components["schemas"]["Overview"]["firing_alerts"] | null | undefined,
): readonly AlertsPanelItem[] {
  const items = firing?.items ?? [];
  return items.map((alert): AlertsPanelItem => ({
    id: alert.rule_id,
    severity: alert.severity,
    title: alert.rule_name,
    at: alert.since,
    href: "/alerts",
  }));
}

/** A single per-bucket point: Unix-ms time and its summed value. */
interface SummedBucket {
  readonly t: number;
  readonly v: number;
}

/**
 * Sum, per time bucket, the points of every live metric
 * series whose name is in `names`, returning `{ t, v }` oldest → newest. Returns `[]` when no
 * matching series has at least two buckets, so a trend is never implied the stream has not actually
 * delivered (design Property 2).
 */
function summedBuckets(
  metrics: ReadonlyMap<string, MetricSeries>,
  names: readonly string[],
): readonly SummedBucket[] {
  if (names.length === 0) return [];
  const nameSet = new Set(names);
  const byBucket = new Map<number, number>();
  for (const series of metrics.values()) {
    if (!nameSet.has(series.name)) continue;
    for (const point of series.points) {
      byBucket.set(point.t, (byBucket.get(point.t) ?? 0) + point.v);
    }
  }
  if (byBucket.size < 2) return [];
  return [...byBucket.keys()]
    .sort((a, b) => a - b)
    .map((t): SummedBucket => ({ t, v: byBucket.get(t) ?? 0 }));
}

/** A no-data `Quantity` (null value, exact) for a headline rate the response did not provide. */
function noDataQuantity(): Quantity {
  return { value: null, exactness: "exact" };
}

/** Fill any missing headline Quantity with a no-data value so every card renders safely. */
function normalizeHeadline(headline: Headline | null | undefined): Headline {
  return {
    decisions_per_s: headline?.decisions_per_s ?? noDataQuantity(),
    deny_ratio: headline?.deny_ratio ?? noDataQuantity(),
    tokens_per_min: headline?.tokens_per_min ?? noDataQuantity(),
    latency_p95_ms: headline?.latency_p95_ms ?? noDataQuantity(),
  };
}
